import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../lib/api.js';
import { withChannel } from '../lib/channels.js';
import { runEmbeddedSignup } from '../lib/embeddedSignup.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/**
 * WhatsApp numbers, and the Page each one answers for.
 *
 * This screen exists because WhatsApp needs two facts that no other channel
 * does, and neither can be guessed:
 *
 *   • which Page a business number speaks for — Meta records no link between
 *     them, so a person says it once;
 *   • whether delivery is actually switched on — a correct callback URL is not
 *     enough, and an account that is not subscribed receives nothing while
 *     looking perfectly configured everywhere else.
 *
 * The second is why every row states its delivery status in words rather than
 * leaving it to be inferred from an empty inbox. WhatsApp never redelivers: a
 * message that arrives while a number is unsubscribed is gone, not late, and
 * that is worth being loud about.
 */
export function WhatsappPanel() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [token, setToken] = useState('');
  const [showImport, setShowImport] = useState(false);

  const numbers = useQuery({
    queryKey: ['whatsapp'],
    queryFn: api.whatsappNumbers,
  });
  const pages = useQuery({ queryKey: ['pages'], queryFn: api.pages });
  const signupConfig = useQuery({
    queryKey: ['whatsapp-signup-config'],
    queryFn: api.whatsappSignupConfig,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['whatsapp'] });

  /**
   * The Meta-hosted flow, and the only route to a number that stays live in the
   * WhatsApp Business app on somebody's phone.
   *
   * Pasting a token cannot reach it: Meta verifies the number by a QR code
   * scanned from inside the app and asks the owner's permission to share their
   * history. The code that comes back expires in thirty seconds, which is why
   * the window and the exchange are one action here rather than two.
   */
  /*
   * Held here rather than read straight from the query, so the field does not
   * fight the operator's typing every time the list refetches.
   */
  const [configDraft, setConfigDraft] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);

  const saveConfig = useMutation({
    mutationFn: api.setWhatsappSignupConfig,
    onSuccess: () => {
      setConfigDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['whatsapp-signup-config'] });
    },
  });

  const signup = useMutation({
    mutationFn: async () => {
      const config = signupConfig.data;
      if (!config?.configId) throw new Error('لم تُضبط نافذة التسجيل بعد');

      const result = await runEmbeddedSignup({
        appId: config.appId,
        configId: config.configId,
      });
      return api.completeWhatsappSignup(result);
    },
    onSuccess: invalidate,
  });

  const importNumbers = useMutation({
    mutationFn: (accessToken: string) => api.importWhatsapp(accessToken),
    onSuccess: () => {
      // Cleared on success, not on submit: a failed import should not make the
      // person go back to Meta for a token they still have.
      setToken('');
      setShowImport(false);
      void invalidate();
    },
  });

  const assign = useMutation({
    mutationFn: (params: { id: string; pageId: string | null }) =>
      api.assignWhatsappPage(params.id, params.pageId),
    onSuccess: invalidate,
  });

  /*
   * Which row a failure belongs to.
   *
   * The error used to render once, at the top of the panel — above a list of
   * seven numbers. Pressing a button at the bottom produced no visible change
   * anywhere near it, so the same button was pressed ten times against a token
   * Meta had already refused. A result belongs beside the control that caused
   * it.
   */
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  /*
   * Asked for once, per number.
   *
   * Not a "disconnect everything" button: the numbers a tenant runs are rarely
   * all wrong at once, and a single control that clears the lot is the kind
   * that gets pressed to fix one row. Re-importing restores whatever was
   * removed, so the cost of removing one too many is a token paste, not a
   * conversation lost.
   */
  const remove = useMutation({
    mutationFn: (id: string) => api.removeWhatsappNumber(id),
    onSuccess: invalidate,
  });

  const subscribe = useMutation({
    mutationFn: (id: string) => api.subscribeWhatsapp(id),
    onMutate: () => setRowError(null),
    onSuccess: () => {
      setRowError(null);
      invalidate();
    },
    onError: (error: unknown, id: string) => {
      setRowError({
        id,
        message: error instanceof Error ? error.message : String(error),
      });
      // The row's own state may have changed even though the call failed —
      // `credentialsInvalidAt` is written by the failure itself.
      invalidate();
    },
  });

  /*
   * Asks Meta rather than reading our own note.
   *
   * "Receiving messages" was, until now, a memory of a subscription that once
   * succeeded — and it stayed on screen unchanged after the credentials behind
   * it were invalidated, while messages quietly went nowhere. A stored success
   * is not a status, so this replaces it with an answer and corrects the row
   * when Meta disagrees.
   */
  const verify = useMutation({
    mutationFn: (id: string) => api.verifyWhatsapp(id),
    onSuccess: invalidate,
  });

  const rows = numbers.data?.numbers ?? [];
  const activePages = pages.data?.pages.filter((page) => page.isActive) ?? [];

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('whatsapp.title')}</h2>
        <div className="row">
          {/* First, and prominent: it is the path that keeps the number on the
              phone, which is what almost everyone actually wants. Pasting a
              token stays available for numbers that live only on Cloud API. */}
          {signupConfig.data?.configId && (
            <button
              type="button"
              className="primary"
              disabled={signup.isPending}
              onClick={() => signup.mutate()}
            >
              <Icon.plus />
              {signup.isPending ? t('whatsapp.connecting') : t('whatsapp.connectNumber')}
            </button>
          )}
          <button
            type="button"
            className="ghost small"
            onClick={() => setShowImport((open) => !open)}
          >
            <Icon.refresh />
            {t('whatsapp.import')}
          </button>
        </div>
      </div>

      <p className="muted small">{t('whatsapp.intro')}</p>

      {/*
       * The configuration id, editable in the place it is used.
       *
       * It lived in an environment variable, which meant an operator who had to
       * change it — and it changes, because Meta issues a new id whenever a
       * configuration is deleted and remade, and a configuration's token type
       * cannot be edited after creation — needed a server and a redeploy for a
       * number that is public in every dialog URL.
       */}
      {signupConfig.data && !signupConfig.data.configId && (
        <div className="notice warn">
          <div>
            <strong>{t('whatsapp.configMissing')}</strong>
            <p className="tiny muted" style={{ margin: '4px 0 0' }}>
              {t('whatsapp.configMissingHint')}
            </p>
          </div>
        </div>
      )}

      {signupConfig.data && (
        <div style={{ margin: '10px 0 4px' }}>
          <button
            type="button"
            className="ghost small"
            onClick={() => setShowConfig((open) => !open)}
          >
            {t('whatsapp.configToggle')}
          </button>

          {showConfig && (
            <div className="inline-form" style={{ marginTop: 8, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <label className="tiny muted" htmlFor="wa-config">
                  {t('whatsapp.configLabel')}
                </label>
                <input
                  id="wa-config"
                  type="text"
                  inputMode="numeric"
                  placeholder="1049798338013958"
                  disabled={!signupConfig.data.canEdit || saveConfig.isPending}
                  value={configDraft ?? signupConfig.data.configId}
                  onChange={(event) => setConfigDraft(event.target.value)}
                />
                <p className="tiny muted" style={{ margin: '4px 0 0', maxWidth: '34rem' }}>
                  {t('whatsapp.configHint')}
                </p>
                {signupConfig.data.source === 'app' && (
                  <p className="tiny muted" style={{ margin: '4px 0 0' }}>
                    {t('whatsapp.configFromEnv')}
                  </p>
                )}
              </div>
              {signupConfig.data.canEdit && (
                <button
                  type="button"
                  disabled={configDraft === null || saveConfig.isPending}
                  onClick={() => saveConfig.mutate(configDraft ?? '')}
                >
                  {t('common.save')}
                </button>
              )}
            </div>
          )}

          {saveConfig.error && <ErrorNotice error={saveConfig.error} />}
        </div>
      )}

      {showImport && (
        <form
          className="card stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (token.trim().length > 20) importNumbers.mutate(token.trim());
          }}
        >
          <label htmlFor="wa-token">{t('whatsapp.tokenLabel')}</label>
          <textarea
            id="wa-token"
            rows={3}
            value={token}
            onChange={(event) => setToken(event.target.value)}
            placeholder="EAA…"
            spellCheck={false}
          />
          <p className="muted small">{t('whatsapp.tokenHint')}</p>
          <div className="row">
            <button type="submit" disabled={importNumbers.isPending || token.trim().length < 20}>
              {importNumbers.isPending ? t('whatsapp.importing') : t('whatsapp.importAction')}
            </button>
            <button type="button" className="ghost" onClick={() => setShowImport(false)}>
              {t('common.cancel')}
            </button>
          </div>
          {importNumbers.isError && <ErrorNotice error={importNumbers.error} />}
          {importNumbers.isSuccess && (
            <p className="ok small">
              {t('whatsapp.imported')
                .replace('{numbers}', String(importNumbers.data.numbers))
                .replace('{accounts}', String(importNumbers.data.accounts))}
            </p>
          )}
        </form>
      )}

      {signup.isError && <ErrorNotice error={signup.error} />}
      {signup.isSuccess && (
        <p className="ok small">
          {signup.data.coexistence ? t('whatsapp.signedUpCoexist') : t('whatsapp.signedUp')}{' '}
          <span dir="ltr">{signup.data.displayNumber}</span>
        </p>
      )}

      {numbers.isError && <ErrorNotice error={numbers.error} />}
      {numbers.isLoading && <div className="loading">{t('common.loading')}</div>}

      {numbers.data && rows.length === 0 && (
        <div className="empty-state">
          <Icon.link />
          <p>{t('whatsapp.none')}</p>
        </div>
      )}

      {assign.isError && <ErrorNotice error={assign.error} />}

      {verify.isError && <ErrorNotice error={verify.error} />}

      {/* Meta's own verdict, in Meta's words when it is bad news. Paraphrasing
          an invalidated token as "something went wrong" is how the last one
          went unnoticed for a day. */}
      {verify.isSuccess && (
        <p className={verify.data.subscribed ? 'ok small' : 'warn small'}>
          {verify.data.subscribed
            ? t('whatsapp.checkOk')
            : `${t('whatsapp.checkBad')}${verify.data.problem ? ` — ${verify.data.problem}` : ''}`}
        </p>
      )}

      <ul className="wa-list">
        {rows.map((number) => {
          const live = Boolean(number.pageId && number.subscribedAt);
          return (
            <li key={number.id} className={live ? 'card wa-row live' : 'card wa-row'}>
              <div className="wa-id">
                <strong dir="ltr">{number.displayNumber}</strong>
                <span className="muted small">{number.verifiedName ?? number.wabaName ?? ''}</span>
                {/*
                 * Carried on the row rather than shown only at the moment of a
                 * click. A refused token changes nothing visible about a number
                 * — it simply stops working — so the row has to say so on its
                 * own, every time the screen is opened, until it is repaired.
                 */}
                {number.credentialsInvalidAt && (
                  <span className="badge err">{t('whatsapp.credentialsDead')}</span>
                )}
                {/* Coexistence numbers behave differently — replies may arrive
                    from a phone, and throughput is capped — so the row says
                    which kind it is. */}
                {number.coexistence && (
                  <span className="tiny muted">{t('whatsapp.coexistence')}</span>
                )}
              </div>

              <label className="wa-assign">
                <span className="muted small">{t('whatsapp.answersFor')}</span>
                <select
                  value={number.pageId ?? ''}
                  disabled={assign.isPending}
                  onChange={(event) =>
                    assign.mutate({
                      id: number.id,
                      pageId: event.target.value || null,
                    })
                  }
                >
                  <option value="">{t('whatsapp.unassigned')}</option>
                  {activePages.map((page) => (
                    <option key={page.id} value={page.id}>
                      {withChannel(t, 'FACEBOOK', page.name)}
                    </option>
                  ))}
                </select>
              </label>

              {/* Said in words. An unsubscribed number is not behind — it is
                  dropping messages that will never be offered again. */}
              <div className="wa-state">
                {live ? (
                  <>
                    <span className="ok small">
                      <Icon.check /> {t('whatsapp.receiving')}
                    </span>
                    <button
                      type="button"
                      className="ghost small"
                      disabled={verify.isPending}
                      onClick={() => verify.mutate(number.id)}
                    >
                      {verify.isPending ? t('whatsapp.checking') : t('whatsapp.check')}
                    </button>
                  </>
                ) : number.pageId ? (
                  <button
                    type="button"
                    className="ghost small"
                    disabled={subscribe.isPending}
                    onClick={() => subscribe.mutate(number.id)}
                  >
                    {t('whatsapp.enable')}
                  </button>
                ) : (
                  <span className="warn small">{t('whatsapp.needsPage')}</span>
                )}

                {/* Last, and quiet: removing is rarely what somebody came here
                    to do, and it is the one action on this row that discards
                    something. */}
                <button
                  type="button"
                  className="danger small"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(t('whatsapp.removeConfirm'))) remove.mutate(number.id);
                  }}
                >
                  {t('whatsapp.remove')}
                </button>
              </div>

              {/*
               * One sentence, not two.
               *
               * A refused token produced both Meta's own English text and our
               * explanation of it, stacked in a column that could not wrap — so
               * the row showed the same fact twice, overlapping and unreadable.
               * When the cause is known, the row says what to do about it and
               * nothing else; the raw text is kept only for a failure we have
               * no better words for.
               */}
              {number.credentialsInvalidAt ? (
                <p className="wa-note warn tiny">{t('whatsapp.credentialsDeadHint')}</p>
              ) : (
                rowError?.id === number.id && (
                  <p className="wa-note warn tiny">{rowError.message}</p>
                )
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
